import { SerializedError } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import React from "react";

import { InvoicingDetailSheetData } from "../../_components/view/InvoiceDetailsSheet";
import { useInvoicingContext } from "../../context/InvoicingContext";

import { TFetchWrapperProps } from "@/components/reusables/FetchWrapper";
import {
  InvoicingActions,
  InvoicingLocalKeys,
  InvoicingQueryParams,
  InvoicingStatus,
} from "@/constants/invoicing.constants";
import useQueryParams from "@/hooks/useQueryParams";
import { IProject } from "@/interfaces/project.interface";
import { ServicesEntity } from "@/interfaces/workdocket.interface";
import { useGetMultipleDocketQuery } from "@/redux/api/docketQuery";
import {
  useGetSingleDocketInvoiceQuery,
  useMuateInvoiceMutation,
} from "@/redux/api/invoicingQuery";
import {
  CreateDocketInvoicing,
  emptyInvoicing,
  emptyInvoicingItems,
} from "@/schemas/invoicing.schema";
import {
  combineDateWithCurrentTime,
  dateFormat,
} from "@/utils/helper-functions/date-time.helper";
import {
  filterEmptyValues,
  parseToFormData,
} from "@/utils/helper-functions/form.helper";

// Types
type MutationResponse =
  | { data: object; error?: undefined }
  | { data?: undefined; error: FetchBaseQueryError | SerializedError };

export type TypeBaseInvoicingSheetContextTypes = {
  formattedFormDefaultValues: CreateDocketInvoicing;
  isLoading: Omit<TFetchWrapperProps, "children">;
  mutateDocketInvoice: (
    data: CreateDocketInvoicing,
    dirtyFields?: object,
  ) => Promise<MutationResponse>;
  setSelectedProjectId: (projectId: string) => void;
  isMutating: boolean;
  projects: IProject[];
  formattedInvoicingDetails: InvoicingDetailSheetData[];
};

// Helpers
export const formatDescription = (
  service: ServicesEntity,
  docketId: number,
): string => {
  const formattedCreatedDate = dateFormat(service?.createdAt, "dd MMM");
  const items = service?.item;
  const itemGroup = items?.itemGroup;
  const groupName = itemGroup?.name;
  const itemName = items?.title;
  const notes = items?.description;

  return `${formattedCreatedDate} \n${groupName}-${itemName}  Note:${notes} #${docketId}`;
};

//Interface
interface DocketInvoicingContextType
  extends TypeBaseInvoicingSheetContextTypes {
  setProjects: React.Dispatch<React.SetStateAction<IProject[]>>;
  setFormattedInvoicingDetails: React.Dispatch<
    React.SetStateAction<InvoicingDetailSheetData[]>
  >;
}

// Context
export const DocketInvoicingContext =
  React.createContext<DocketInvoicingContextType>(
    {} as DocketInvoicingContextType,
  );

// Provider Component
export const DocketInvoicingProvider = ({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element => {
  const { getAllValue } = useQueryParams();
  const docketIds = getAllValue(InvoicingQueryParams.DOCKET_ID);
  const [selectedProjectId, setSelectedProjectId] = React.useState("");
  const [projects, setProjects] = React.useState<IProject[]>([]);
  const [formattedInvoicingDetails, setFormattedInvoicingDetails] =
    React.useState<InvoicingDetailSheetData[]>([]);
  const { getLocalStorageData, xeroTrackingItems } = useInvoicingContext();

  const invoicingActions = React.useMemo(
    () =>
      getLocalStorageData() || {
        action: InvoicingActions.CREATE,
        for: InvoicingLocalKeys.DOCKET,
      },
    [getLocalStorageData],
  );

  // Queries
  const { data: multipleDockets, ...isMultipleDocketsLoading } =
    useGetMultipleDocketQuery(
      { docketIds: docketIds || [] },
      {
        skip:
          !docketIds?.length ||
          invoicingActions.for !== InvoicingLocalKeys.DOCKET ||
          invoicingActions.action !== InvoicingActions.CREATE,
      },
    );

  const { data: singleDocketInvoice, ...isSingleInvoiceLoading } =
    useGetSingleDocketInvoiceQuery(
      { id: docketIds?.[0] ?? "" },
      {
        skip:
          !docketIds?.length ||
          invoicingActions.for !== InvoicingLocalKeys.DOCKET ||
          invoicingActions.action === InvoicingActions.CREATE,
      },
    );

  const [mutateInvoice, { isLoading: isMutating }] = useMuateInvoiceMutation();

  const constructDefaultValues = (): CreateDocketInvoicing => {
    if (
      invoicingActions.action === InvoicingActions.CREATE &&
      multipleDockets
    ) {
      const itemSheets =
        multipleDockets.dockets?.flatMap(
          (docket) =>
            docket.services?.map((service) => ({
              ...emptyInvoicingItems,
              description: formatDescription(service, docket.docketId),
              quantity: service.quantity ?? 0,
              price:
                service?.item?.currentPrice ?? service?.item?.defaultPrice ?? 0,
              ...((service?.item.invoiceItem || xeroTrackingItems) && {
                itemId: service?.item?.invoiceItem?.id ?? undefined,
                trackingName:
                  service?.item?.invoiceItem?.trackingName ?? undefined,
                account: service?.item?.invoiceItem?.accountCode ?? "",
                tax: service?.item?.invoiceItem?.tax ?? 0,
                trackingOption:
                  service?.item?.invoiceItem?.trackingOption ?? undefined,
                ...(xeroTrackingItems?.length && {
                  trackingName: xeroTrackingItems[0]?.id ?? undefined,
                  trackingOption:
                    xeroTrackingItems[0].options?.[0]?.trackingOptionID ??
                    undefined,
                }),
              }),
            })) || [],
        ) ?? [];

      return {
        ...emptyInvoicing,
        action: InvoicingActions.CREATE,
        projectId: multipleDockets.projects?.[0]?.id ?? "",
        itemSheets: itemSheets.filter(Boolean),
      };
    }

    if (singleDocketInvoice) {
      return {
        id: singleDocketInvoice?.id,
        referenceNumber: singleDocketInvoice?.referenceNumber ?? "",
        dueDate: undefined,
        projectId: singleDocketInvoice?.projectId ?? "",
        customerId: singleDocketInvoice?.customerId ?? "",
        status: singleDocketInvoice?.status ?? "",
        invoiceFor: InvoicingLocalKeys.DOCKET,
        action:
          invoicingActions?.action === InvoicingActions.VIEW
            ? InvoicingActions.VIEW
            : InvoicingActions.UPDATE,
        documents: singleDocketInvoice?.documents ?? [],
        notes: singleDocketInvoice?.note ?? "",
        itemSheets:
          singleDocketInvoice?.items?.map((item) => ({
            ...emptyInvoicingItems,
            itemDetailId: item?.id ?? "",
            itemId: item?.invoiceItem?.id ?? undefined,
            defaultItemName: item?.invoiceItem?.title ?? "",
            description: item?.description ?? "",
            quantity: item?.quantity ?? 0,
            trackingName: item?.trackingName ?? undefined,
            trackingOption: item?.trackingOption ?? undefined,
            account: item?.accountCode ?? "",
            discount: item?.discount ?? 0,
            price: item?.price ?? 0,
            tax: item?.taxPercentage ?? 0,
            docketId: singleDocketInvoice?.dockets?.[0]?.id ?? "",
          })) ?? [],
      };
    }

    return {
      ...emptyInvoicing,
      itemSheets: [emptyInvoicingItems],
    };
  };

  const formattedFormDefaultValues = React.useMemo(constructDefaultValues, [
    multipleDockets,
    singleDocketInvoice,
    invoicingActions.action,
    xeroTrackingItems,
  ]);

  const isLoading = isMultipleDocketsLoading || isSingleInvoiceLoading; //It is named as loading but it has multiple properties ie. error, refetch, more,,

  const mutateDocketInvoice = async (
    values: CreateDocketInvoicing,
    dirtyFields?: object,
  ): Promise<MutationResponse> => {
    const isInvoiceOfDraftStatus = values.status === InvoicingStatus.DRAFT;
    const docketIdsToSend = isInvoiceOfDraftStatus
      ? docketIds?.concat(values.selectedDocketIds ?? [])
      : docketIds;

    const dataToSend = {
      ...values,
      docketIds: docketIdsToSend,
      customerId: multipleDockets?.dockets?.[0]?.customerId,
      projectId: selectedProjectId || multipleDockets?.projects?.[0]?.id,
      dueDate: values?.dueDate
        ? combineDateWithCurrentTime(values?.dueDate as Date)
        : undefined,
    };
    const existingDocuments = values.documents?.filter(
      (doc) => typeof doc === "string" && doc.startsWith("http"),
    );

    const httpDocuments = values.documents?.filter(
      (doc) => typeof doc === "object",
    );
    const filteredValue = filterEmptyValues(dataToSend);
    const formData = parseToFormData(
      filteredValue,
      ["documents"],
      invoicingActions.action === InvoicingActions.UPDATE ? dirtyFields : {},
    );
    if (
      invoicingActions.action === InvoicingActions.CREATE &&
      !Object.prototype.hasOwnProperty.call(dirtyFields, "dueDate")
    ) {
      formData.delete("dueDate");
    }
    if (httpDocuments?.length === 0) {
      formData.delete("documents");
    }
    formData.append("existingDocuments", JSON.stringify(existingDocuments));
    const res = await mutateInvoice({
      data: formData,
      method:
        invoicingActions.action === InvoicingActions.UPDATE ||
        isInvoiceOfDraftStatus
          ? "PUT"
          : "POST",
      id:
        invoicingActions.action === InvoicingActions.UPDATE ||
        isInvoiceOfDraftStatus
          ? values?.id
          : "",
    });

    return res;
  };

  React.useEffect(() => {
    if (
      invoicingActions.action === InvoicingActions.CREATE &&
      multipleDockets
    ) {
      setProjects(multipleDockets.projects);

      const formattedDocketsArr = multipleDockets?.dockets?.map((docket) => ({
        id: docket?.docketId,
        createdAt: docket?.createdAt,
        reportType: docket?.report,
        customerName: docket?.customer?.name ?? "",
        itemGroup: docket?.services?.map((service) => ({
          name: service?.item?.title ?? "---",
          quantity: service?.quantity,
        })),
        jobDescription: docket?.jobDescription,
        siteManager: docket?.siteManager,
        projectName: docket?.project?.name,
      }));

      setFormattedInvoicingDetails(
        // Filter out any undefined values and ensure the array contains only valid InvoicingDetailSheetData objects
        (formattedDocketsArr ?? []).filter(
          Boolean,
        ) as InvoicingDetailSheetData[],
      );
    }

    if (
      (invoicingActions.action === InvoicingActions.VIEW ||
        invoicingActions.action === InvoicingActions.UPDATE) &&
      singleDocketInvoice
    ) {
      const formattedDocketsArr = singleDocketInvoice?.dockets?.map(
        (docket) => ({
          id: docket?.docketId,
          createdAt: docket?.createdAt,
          reportType: docket?.report,
          status: docket?.status,
          customerName: singleDocketInvoice?.customer?.name ?? "",
          itemGroup: singleDocketInvoice?.items?.map((service) => ({
            name: service?.invoiceItem?.title ?? "---",
            quantity: service?.quantity,
          })),
          jobDescription: docket?.jobDescription,
          siteManager: docket?.siteManager,
          projectName: singleDocketInvoice?.project?.name,
        }),
      );

      setFormattedInvoicingDetails(
        // Filter out any undefined values and ensure the array contains only valid InvoicingDetailSheetData objects
        (formattedDocketsArr ?? []).filter(
          Boolean,
        ) as InvoicingDetailSheetData[],
      );
    }
  }, [multipleDockets, singleDocketInvoice, invoicingActions]);

  return (
    <DocketInvoicingContext.Provider
      value={{
        setSelectedProjectId,
        isMutating,
        isLoading,
        formattedFormDefaultValues,
        mutateDocketInvoice,
        projects,
        formattedInvoicingDetails,
        setProjects,
        setFormattedInvoicingDetails,
      }}
    >
      {children}
    </DocketInvoicingContext.Provider>
  );
};

export const useDocketInvoicing = (): DocketInvoicingContextType =>
  React.useContext(DocketInvoicingContext);
